import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@/components/base/Loading";
import { FAQ_CATEGORIES, FAQ_ITEMS, faqPreviewUrl, type FaqItem } from "@/pages/help/faqData";
import {
  hasDraft,
  type FaqContentOverride,
  type FaqContentPayload,
  type FaqVersionRow,
} from "@/pages/help/faqContent";
import { buildFaqStats, type FaqStatRow } from "@/pages/admin/help/faqStats";
import { downloadCsv, type CsvColumn } from "@/lib/csv";
import { formatNumber, formatRelativeTime } from "@/lib/format";
import type { FaqFeedbackRow } from "@/pages/admin/help/hooks/useFaqFeedback";
import type { FaqOptimizationRow } from "@/pages/admin/help/hooks/useFaqOptimization";
import FaqContentEditor from "@/pages/admin/help/components/FaqContentEditor";
import FaqBatchPublishModal from "@/pages/admin/help/components/FaqBatchPublishModal";

interface FaqFeedbackSummaryProps {
  rows: FaqFeedbackRow[];
  loading: boolean;
  contentOverrides: Record<string, FaqContentOverride>;
  savingContentKey: string | null;
  onSaveDraft: (item: FaqItem, payload: FaqContentPayload) => void;
  onPublish: (item: FaqItem, payload: FaqContentPayload) => void;
  onReset: (item: FaqItem) => void;
  fetchVersions: (qKey: string) => Promise<FaqVersionRow[]>;
  onRollback: (item: FaqItem, version: FaqVersionRow) => void;
  onBatchPublish: (items: FaqItem[]) => Promise<void>;
  optimizations: Record<string, FaqOptimizationRow>;
  savingKey: string | null;
  onResolve: (qKey: string) => void;
  onUndoResolved: (qKey: string) => void;
  rateThreshold: number;
  minNotHelpful: number;
}

type SortKey = "notHelpful" | "rate" | "total";

const KNOWN_KEYS = new Set(FAQ_ITEMS.map((item) => item.qKey));
const CATEGORY_LABELS: Record<string, string> = FAQ_CATEGORIES.reduce(
  (acc, category) => {
    acc[category.id] = category.labelKey;
    return acc;
  },
  {} as Record<string, string>,
);

export default function FaqFeedbackSummary({
  rows,
  loading,
  contentOverrides,
  savingContentKey,
  onSaveDraft,
  onPublish,
  onReset,
  fetchVersions,
  onRollback,
  onBatchPublish,
  optimizations,
  savingKey,
  onResolve,
  onUndoResolved,
  rateThreshold,
  minNotHelpful,
}: FaqFeedbackSummaryProps) {
  const { t } = useTranslation();
  const [sort, setSort] = useState<SortKey>("notHelpful");
  const [activeCat, setActiveCat] = useState("all");
  const [onlyOptimize, setOnlyOptimize] = useState(false);
  const [onlyDraft, setOnlyDraft] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [versions, setVersions] = useState<FaqVersionRow[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchPublishing, setBatchPublishing] = useState(false);

  const stats = useMemo<FaqStatRow[]>(
    () => buildFaqStats(rows, optimizations, contentOverrides, rateThreshold, minNotHelpful),
    [rows, optimizations, contentOverrides, rateThreshold, minNotHelpful],
  );

  const questionLabel = (qKey: string) => (KNOWN_KEYS.has(qKey) ? t(qKey) : t("helpFeedback.unknownQuestion"));
  const categoryLabel = (catId: string) => {
    const key = CATEGORY_LABELS[catId];
    return key ? t(key) : t("helpFeedback.noCategory");
  };

  const categoryOptions = useMemo(() => {
    const counts = new Map<string, number>();
    stats.forEach((item) => {
      const key = item.category || "";
      counts.set(key, (counts.get(key) ?? 0) + item.total);
    });
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [stats]);

  const draftItems = useMemo<FaqItem[]>(
    () =>
      stats
        .filter((item) => hasDraft(contentOverrides[item.qKey]))
        .map((item) => FAQ_ITEMS.find((faq) => faq.qKey === item.qKey))
        .filter((faq): faq is FaqItem => Boolean(faq)),
    [stats, contentOverrides],
  );

  const filtered = useMemo(() => {
    let list = activeCat === "all" ? stats : stats.filter((item) => (item.category || "") === activeCat);
    if (onlyOptimize) list = list.filter((item) => item.needsOptimize || item.resolved);
    if (onlyDraft) list = list.filter((item) => hasDraft(contentOverrides[item.qKey]));
    return [...list].sort((a, b) => {
      if (sort === "rate") return b.rate - a.rate || b.notHelpful - a.notHelpful;
      if (sort === "total") return b.total - a.total || b.notHelpful - a.notHelpful;
      return b.notHelpful - a.notHelpful || b.total - a.total;
    });
  }, [stats, activeCat, sort, onlyOptimize, onlyDraft, contentOverrides]);

  const topItems = useMemo(
    () =>
      stats
        .filter((item) => item.notHelpful > 0)
        .sort((a, b) => b.notHelpful - a.notHelpful || b.total - a.total)
        .slice(0, 6),
    [stats],
  );
  const maxNotHelpful = topItems.length > 0 ? topItems[0].notHelpful : 1;

  const optimizeCount = useMemo(() => stats.filter((item) => item.needsOptimize).length, [stats]);

  const sortOptions: { key: SortKey; label: string }[] = [
    { key: "notHelpful", label: t("helpFeedback.sortNotHelpful") },
    { key: "rate", label: t("helpFeedback.sortRate") },
    { key: "total", label: t("helpFeedback.sortTotal") },
  ];

  const statusLabel = (item: FaqStatRow) =>
    item.resolved
      ? t("helpFeedback.statusResolved")
      : item.needsOptimize
        ? t("helpFeedback.statusOptimize")
        : t("helpFeedback.statusWatching");

  const openEditor = (qKey: string) => {
    setEditingKey(qKey);
    setVersions([]);
    setVersionsLoading(true);
    void fetchVersions(qKey).then((list) => {
      setVersions(list);
      setVersionsLoading(false);
    });
  };

  const confirmBatchPublish = async () => {
    setBatchPublishing(true);
    try {
      await onBatchPublish(draftItems);
    } finally {
      setBatchPublishing(false);
      setBatchOpen(false);
    }
  };

  const exportCsv = () => {
    const exportRows = [...stats]
      .filter((item) => item.notHelpful > 0)
      .sort((a, b) => b.notHelpful - a.notHelpful || b.rate - a.rate);
    const columns: CsvColumn<FaqStatRow>[] = [
      { key: "question", label: t("helpFeedback.colQuestion"), value: (row) => questionLabel(row.qKey) },
      { key: "category", label: t("helpFeedback.colCategory"), value: (row) => categoryLabel(row.category) },
      { key: "helpful", label: t("helpFeedback.colHelpful"), value: (row) => row.helpful },
      { key: "notHelpful", label: t("helpFeedback.colNotHelpful"), value: (row) => row.notHelpful },
      { key: "total", label: t("helpFeedback.colTotal"), value: (row) => row.total },
      { key: "rate", label: t("helpFeedback.colRate"), value: (row) => `${Math.round(row.rate * 100)}%` },
      { key: "status", label: t("helpFeedback.colStatus"), value: (row) => statusLabel(row) },
      { key: "note", label: t("helpFeedback.colNote"), value: (row) => row.note ?? "" },
      { key: "owner", label: t("helpFeedback.colOwner"), value: (row) => row.owner ?? "" },
      { key: "last", label: t("helpFeedback.colLast"), value: (row) => row.lastAt ?? "" },
    ];
    const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    downloadCsv(`faq-not-helpful-top-${stamp}.csv`, columns, exportRows);
  };

  const editingItem = editingKey ? FAQ_ITEMS.find((item) => item.qKey === editingKey) ?? null : null;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="space-y-3 rounded-lg border border-background-200 bg-background-50 p-5">
          {[0, 1, 2].map((item) => (
            <Skeleton key={item} className="h-14 w-full" />
          ))}
        </div>
        <div className="space-y-3 rounded-lg border border-background-200 bg-background-50 p-5">
          {[0, 1, 2, 3].map((item) => (
            <Skeleton key={item} className="h-10 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center rounded-lg border border-background-200 bg-background-50 px-6 py-16 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-questionnaire-line text-2xl" />
        </span>
        <p className="mt-4 font-heading text-base font-semibold text-foreground-950">{t("helpFeedback.empty")}</p>
        <p className="mt-2 max-w-md text-sm text-foreground-600">{t("helpFeedback.emptyDesc")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-background-200 bg-background-50 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-heading text-base font-semibold text-foreground-950">{t("helpFeedback.topTitle")}</h2>
            <p className="mt-1 text-xs text-foreground-500">{t("helpFeedback.topSub")}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {optimizeCount > 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-700">
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-error-warning-line" />
                </span>
                {t("helpFeedback.optimizeCount", { count: formatNumber(optimizeCount) })}
              </span>
            )}
            <button
              type="button"
              onClick={exportCsv}
              disabled={topItems.length === 0}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-download-2-line" />
              </span>
              {t("helpFeedback.exportTop")}
            </button>
          </div>
        </div>

        {topItems.length === 0 ? (
          <div className="mt-5 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-10 text-center">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 text-primary-700">
              <i className="ri-checkbox-circle-line text-xl" />
            </span>
            <p className="mt-3 text-sm font-medium text-foreground-950">{t("helpFeedback.topEmpty")}</p>
            <p className="mt-1 text-xs text-foreground-600">{t("helpFeedback.topEmptyDesc")}</p>
          </div>
        ) : (
          <div className="mt-5 space-y-3">
            {topItems.map((item, index) => (
              <div key={item.qKey} className="flex items-start gap-3">
                <span
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-sm font-semibold ${
                    index === 0 ? "bg-accent-500 text-accent-950" : "bg-background-200 text-foreground-700"
                  }`}
                >
                  {index + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-1.5">
                      <a
                        href={faqPreviewUrl(item.qKey)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={t("helpFeedback.previewTitle")}
                        className="group flex min-w-0 items-center gap-1.5 text-sm font-medium text-foreground-950 transition-colors hover:text-primary-700"
                      >
                        <span className="min-w-0 truncate">{questionLabel(item.qKey)}</span>
                        <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center text-foreground-400 transition-colors group-hover:text-primary-600">
                          <i className="ri-external-link-line" />
                        </span>
                      </a>
                      {KNOWN_KEYS.has(item.qKey) && (
                        <button
                          type="button"
                          onClick={() => openEditor(item.qKey)}
                          title={t("helpFeedback.editAnswer")}
                          className="flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-md border border-transparent text-foreground-400 transition-colors hover:border-background-300 hover:text-primary-600"
                        >
                          <i className="ri-edit-line" />
                        </button>
                      )}
                    </div>
                    <span className="shrink-0 text-xs text-foreground-600">
                      {t("helpFeedback.notHelpfulUnit", { count: formatNumber(item.notHelpful) })}
                    </span>
                  </div>
                  <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-background-200">
                    <div
                      className="h-full rounded-full bg-accent-500"
                      style={{ width: `${Math.max(6, (item.notHelpful / maxNotHelpful) * 100)}%` }}
                    />
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-foreground-500">
                    <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2 py-0.5 text-secondary-900">
                      {categoryLabel(item.category)}
                    </span>
                    {hasDraft(contentOverrides[item.qKey]) && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2 py-0.5 text-secondary-900">
                        <span className="flex h-3 w-3 items-center justify-center">
                          <i className="ri-draft-line" />
                        </span>
                        {t("helpFeedback.draftBadge")}
                      </span>
                    )}
                    {item.edited && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-background-200 px-2 py-0.5 text-foreground-700">
                        <span className="flex h-3 w-3 items-center justify-center">
                          <i className="ri-edit-2-line" />
                        </span>
                        {t("helpFeedback.contentEditedBadge")}
                      </span>
                    )}
                    {item.resolved && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2 py-0.5 text-primary-700">
                        <span className="flex h-3 w-3 items-center justify-center">
                          <i className="ri-checkbox-circle-line" />
                        </span>
                        {t("helpFeedback.resolvedBadge")}
                      </span>
                    )}
                    {item.needsOptimize && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-2 py-0.5 text-accent-700">
                        <span className="flex h-3 w-3 items-center justify-center">
                          <i className="ri-error-warning-line" />
                        </span>
                        {t("helpFeedback.optimizeBadge")}
                      </span>
                    )}
                    <span>{t("helpFeedback.helpfulUnit", { count: formatNumber(item.helpful) })}</span>
                    <span>{t("helpFeedback.totalUnit", { count: formatNumber(item.total) })}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-lg border border-background-200 bg-background-50 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-heading text-base font-semibold text-foreground-950">{t("helpFeedback.tableTitle")}</h2>
            <p className="mt-1 text-xs text-foreground-500">{t("helpFeedback.tableSub")}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {draftItems.length > 0 && (
              <button
                type="button"
                onClick={() => setBatchOpen(true)}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-primary-300 bg-primary-50 px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-100"
              >
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-send-plane-line" />
                </span>
                {t("helpFeedback.batchPublishCount", { count: draftItems.length })}
              </button>
            )}
            <div className="inline-flex rounded-full border border-background-300 bg-background-100 p-1">
              {sortOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setSort(option.key)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors ${
                    sort === option.key ? "bg-background-50 text-foreground-950" : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setActiveCat("all")}
            className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
              activeCat === "all"
                ? "bg-secondary-500 text-background-50"
                : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
            }`}
          >
            {t("helpFeedback.allCategories")}
          </button>
          {categoryOptions.map(([catId, count]) => (
            <button
              key={catId || "unknown"}
              type="button"
              onClick={() => setActiveCat(catId)}
              className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                activeCat === catId
                  ? "bg-secondary-500 text-background-50"
                  : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
              }`}
            >
              {categoryLabel(catId)}
              <span
                className={`flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-medium ${
                  activeCat === catId ? "bg-background-50/25 text-background-50" : "bg-background-200 text-foreground-600"
                }`}
              >
                {formatNumber(count)}
              </span>
            </button>
          ))}
          <button
            type="button"
            onClick={() => setOnlyOptimize((prev) => !prev)}
            className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
              onlyOptimize
                ? "bg-accent-500 text-accent-950"
                : "border border-background-300 bg-background-50 text-foreground-700 hover:border-accent-300"
            }`}
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-filter-3-line" />
            </span>
            {t("helpFeedback.onlyOptimize")}
          </button>
          <button
            type="button"
            onClick={() => setOnlyDraft((prev) => !prev)}
            className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
              onlyDraft
                ? "bg-primary-500 text-background-50"
                : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
            }`}
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-draft-line" />
            </span>
            {t("helpFeedback.onlyDraft")}
            <span
              className={`flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-medium ${
                onlyDraft ? "bg-background-50/25 text-background-50" : "bg-background-200 text-foreground-600"
              }`}
            >
              {formatNumber(draftItems.length)}
            </span>
          </button>
        </div>

        <div className="mt-4 overflow-hidden rounded-lg border border-background-200">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[920px] border-collapse text-sm">
              <thead>
                <tr className="bg-background-100 text-left text-xs text-foreground-600">
                  <th className="px-4 py-3 font-medium">{t("helpFeedback.colQuestion")}</th>
                  <th className="px-4 py-3 font-medium">{t("helpFeedback.colCategory")}</th>
                  <th className="w-20 px-4 py-3 text-right font-medium">{t("helpFeedback.colHelpful")}</th>
                  <th className="w-20 px-4 py-3 text-right font-medium">{t("helpFeedback.colNotHelpful")}</th>
                  <th className="w-24 px-4 py-3 text-right font-medium">{t("helpFeedback.colRate")}</th>
                  <th className="w-28 px-4 py-3 text-right font-medium">{t("helpFeedback.colLast")}</th>
                  <th className="w-44 px-4 py-3 text-right font-medium">{t("helpFeedback.colStatus")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr className="border-t border-background-200">
                    <td colSpan={7} className="px-4 py-12 text-center">
                      <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                        <i className={onlyDraft ? "ri-draft-line text-xl" : "ri-filter-3-line text-xl"} />
                      </span>
                      <p className="mt-3 text-sm font-medium text-foreground-950">
                        {onlyDraft ? t("helpFeedback.draftOnlyEmpty") : t("helpFeedback.ledgerEmpty")}
                      </p>
                      <p className="mt-1 text-xs text-foreground-600">
                        {onlyDraft ? t("helpFeedback.draftOnlyEmptyDesc") : t("helpFeedback.tableSub")}
                      </p>
                    </td>
                  </tr>
                ) : (
                  filtered.map((item) => (
                    <tr key={item.qKey} className="border-t border-background-200">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <a
                            href={faqPreviewUrl(item.qKey)}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={t("helpFeedback.previewTitle")}
                            className="group inline-flex items-center gap-1.5 text-sm text-foreground-950 transition-colors hover:text-primary-700"
                          >
                            <span>{questionLabel(item.qKey)}</span>
                            <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center text-foreground-400 transition-colors group-hover:text-primary-600">
                              <i className="ri-external-link-line" />
                            </span>
                          </a>
                          {KNOWN_KEYS.has(item.qKey) && (
                            <button
                              type="button"
                              onClick={() => openEditor(item.qKey)}
                              title={t("helpFeedback.editAnswer")}
                              className="flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-md border border-transparent text-foreground-400 transition-colors hover:border-background-300 hover:text-primary-600"
                            >
                              <i className="ri-edit-line" />
                            </button>
                          )}
                          {hasDraft(contentOverrides[item.qKey]) && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900">
                              <span className="flex h-3 w-3 items-center justify-center">
                                <i className="ri-draft-line" />
                              </span>
                              {t("helpFeedback.draftBadge")}
                            </span>
                          )}
                        </div>
                        {(item.note || item.owner || item.resolvedBy) && (
                          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-foreground-500">
                            {item.note && <span>{t("helpFeedback.notePrefix")}：{item.note}</span>}
                            {item.owner && <span>{t("helpFeedback.ownerPrefix")}：{item.owner}</span>}
                            {item.resolvedBy && <span>{t("helpFeedback.markedBy", { name: item.resolvedBy })}</span>}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900">
                          {categoryLabel(item.category)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-foreground-600">{formatNumber(item.helpful)}</td>
                      <td className="px-4 py-3 text-right font-medium text-accent-700">{formatNumber(item.notHelpful)}</td>
                      <td className="px-4 py-3 text-right text-foreground-700">{Math.round(item.rate * 100)}%</td>
                      <td className="px-4 py-3 text-right text-xs text-foreground-500">
                        {item.lastAt ? formatRelativeTime(item.lastAt) : "--"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          {item.resolved ? (
                            <button
                              type="button"
                              onClick={() => onUndoResolved(item.qKey)}
                              disabled={savingKey === item.qKey}
                              className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full bg-primary-100 px-2.5 py-1 text-[11px] font-medium text-primary-700 transition-colors hover:bg-primary-200 disabled:opacity-60"
                            >
                              <span className="flex h-3 w-3 items-center justify-center">
                                <i className={savingKey === item.qKey ? "ri-loader-4-line animate-spin" : "ri-checkbox-circle-line"} />
                              </span>
                              {t("helpFeedback.unmarkResolved")}
                            </button>
                          ) : item.needsOptimize ? (
                            <button
                              type="button"
                              onClick={() => onResolve(item.qKey)}
                              disabled={savingKey === item.qKey}
                              className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-accent-300 bg-accent-50 px-2.5 py-1 text-[11px] font-medium text-accent-700 transition-colors hover:bg-accent-100 disabled:opacity-60"
                            >
                              <span className="flex h-3 w-3 items-center justify-center">
                                <i className={savingKey === item.qKey ? "ri-loader-4-line animate-spin" : "ri-tools-line"} />
                              </span>
                              {t("helpFeedback.markResolved")}
                            </button>
                          ) : (
                            <span className="text-xs text-foreground-400">{t("helpFeedback.statusWatching")}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {editingItem && (
        <FaqContentEditor
          key={editingItem.qKey}
          item={editingItem}
          override={contentOverrides[editingItem.qKey]}
          saving={savingContentKey === editingItem.qKey}
          versions={versions}
          versionsLoading={versionsLoading}
          onClose={() => setEditingKey(null)}
          onSaveDraft={(payload) => {
            onSaveDraft(editingItem, payload);
            setEditingKey(null);
          }}
          onPublish={(payload) => {
            onPublish(editingItem, payload);
            setEditingKey(null);
          }}
          onReset={() => {
            onReset(editingItem);
            setEditingKey(null);
          }}
          onRollback={(version) => {
            onRollback(editingItem, version);
            setEditingKey(null);
          }}
        />
      )}

      {batchOpen && (
        <FaqBatchPublishModal
          items={draftItems}
          saving={batchPublishing}
          onClose={() => setBatchOpen(false)}
          onConfirm={() => void confirmBatchPublish()}
        />
      )}
    </div>
  );
}